import { defineStore } from 'pinia';
import { ElMessage } from 'element-plus';
import router from '@/router';
import {
  loginApi,
  logoutApi,
  myEnterprises,
  switchEnterprise,
  switchProduct,
  type UserInfoResponse,
  type MyEnterpriseItem,
  type EnterpriseProductRole,
  type ProductAccessItem
} from '@/api/auth';
import {
  getEnterpriseIdFromUrl,
  buildEnterpriseSwitchUrl,
  withEnterpriseId
} from '@/utils/enterprise-context';
import { getProfileDetail } from '@/api/account';
import { i18n } from '@/locales';
import { UserRole, System, SystemRole, SystemProductId } from '@/router/config/roles';
import { redirectToLogin } from '@/utils/auth';
import { UserRoleName, UserRoleNameToIdMap } from '@/router/config/roles';
import {
  getEnterpriseInfo
} from '@/api/company'
import { ProductId } from '@/enum/account';
import {
  getValidToken,
  deleteCookie
} from '@/utils/auth'
import { getSystemType } from '@/config/env';
import {
  routerWhiteList,
  pathIsInRouterWhiteList
} from '@/router/guards';

import { openNoAccessPermissionModal } from '@/components/business-components/dialog';
import { useChatStore } from './chat';
interface UserState {
  id: number;
  token: string;
  roleId: UserRole | null;
  roleIsValidate: boolean;
  roles: string[];
  permissions: string[];
  profile: UserInfoResponse | null;
  isLoggedIn: boolean;
  userSystem: 'business' | 'agent' | null;
  productId: number;
  chatToken: string;
  /** 当前企业ID，权威来源是地址栏的 company_id；URL 上没有时由服务端默认企业回填 */
  enterpriseId: number | null;
  /** 账号所属的全部企业（多企业切换器的数据源） */
  enterprises: MyEnterpriseItem[];
  /** 当前企业下可访问的产品（初始化时由 switch-product 返回），产品切换器的数据源 */
  enterpriseProducts: ProductAccessItem[];
}
// 获取有效token的函数：优先从cookie获取，如果没有则从localStorage获取
const validRoleIds = Object.values(UserRole).filter((value) => typeof value === 'number') as number[];
export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    id: 0,
    token: getValidToken() || '',
    roleId: null,
    roles: [],
    permissions: [],
    profile: null,
    isLoggedIn: false,
    roleIsValidate: false,
    userSystem: null,
    productId: 2,
    // id 57 pddlll-YXJ 的 token
    chatToken: '',
    // 初始化即从地址栏取，保证 bootstrap 的第一个请求就带上正确的 x-enterprise-id
    enterpriseId: getEnterpriseIdFromUrl(),
    enterprises: [],
    enterpriseProducts: [],
  }),

  getters: {
    // isLoggedIn: (state) => !!state.token,
    id: (state) => state.profile?.id,
    userName: (state) => state.profile?.name || state.profile?.username || '',
    userEmail: (state) => state.profile?.email || '',
    userAvatar: (state) => state.profile?.avatar || '',
    // 不再回落到写死的 1：未知时返回 null，请求拦截器会跳过 x-enterprise-id，由后端取默认企业
    userEnterpriseId: (state) => state.enterpriseId,
    userProjectId: (state) => state.profile?.projectId || '',
    userChatToken: (state) => state.profile?.chatToken || '',
    userChatUserId: (state) => state.profile?.streamChatId || '',
  },

  actions: {

    // 登录
    async login(params: { username: string; password: string }) {
      try {
        const response = await loginApi(params);
        
        // 保存 token
        this.token = response.accessToken;
        
        localStorage.setItem('lincId', response.lincId);
        
        // 登录成功后获取用户信息（设置角色）
        await this.getUserInfo();

        ElMessage.success(i18n.global.t('login.loginSuccess'));
        return response;
      } catch (error: any) {
        ElMessage.error(error.message || i18n.global.t('login.loginFailed'));
        throw error;
      }
    },
    // 拉取账号所属的全部企业，作为企业切换器的数据源
    async fetchMyEnterprises() {
      try {
        this.enterprises = (await myEnterprises()) || [];
      } catch (error) {
        console.error('获取企业列表失败:', error);
        this.enterprises = [];
      }
    },

    /**
     * 判断账号在某企业下对「当前端」的访问能力
     * - ok     : 有当前产品权限，可直接切换
     * - talent : 无当前产品权限但有 Talent 权限，引导去 Talent（仅企业端，猎头端不涉及多产品）
     * - none   : 在该企业下没有可用产品
     */
    resolveEnterpriseProductAccess(products?: EnterpriseProductRole[]): 'ok' | 'talent' | 'none' {
      const list = products || [];
      if (list.some((p) => p.productId === this.productId)) {
        return 'ok';
      }
      if (this.userSystem !== System.agent && list.some((p) => p.productId === ProductId.TALENT)) {
        return 'talent';
      }
      return 'none';
    },

    /**
     * 切换企业：改写地址栏的 company_id 后整页刷新
     * 无权限时停留在当前企业，仅提示
     */
    async switchToEnterprise(enterpriseId: number) {
      console.log('enterpriseId', enterpriseId)
      if (enterpriseId === this.enterpriseId) {
        return;
      }
      const target = this.enterprises.find((item) => item.enterpriseId === enterpriseId);
      if (!target) {
        return;
      }

      const access = this.resolveEnterpriseProductAccess(target.products);

      // 无当前产品权限但有 Talent：引导去 Talent，不切换本系统的企业
      if (access === 'talent') {
        openNoAccessPermissionModal({
          message: i18n.global.t('common.noCareerAccess'),
          tips: i18n.global.t('common.contactAdminToEnable'),
          confirmButtonText: i18n.global.t('common.enterTalentSystem'),
        })
          .then(async () => {
            // 目标是「用户刚在下拉里选中的那个企业」，不是当前企业。
            // 不带 company_id 的话 Talent 会回落到它自己的默认企业，看到的就是另一家公司
            try {
              await switchEnterprise({ enterpriseId });
            } catch (error) {
              // 服务端 current 没记上不影响本次跳转（Talent 靠 URL 的 company_id 定位）
              console.error('切换企业失败:', error);
            }
            window.location.href = withEnterpriseId(import.meta.env.VITE_TALENT_URL, enterpriseId);
          })
          .catch(() => {
            // 用户关闭弹窗，留在当前企业
          });
        return;
      }

      // 目标企业下没有任何可用产品：提示后留在当前企业
      if (access === 'none') {
        openNoAccessPermissionModal({
          message: i18n.global.t('common.noAccessPermission'),
          confirmButtonText: i18n.global.t('common.ok'),
          confirmButtonProps: { type: 'default' },
        }).catch(() => {
          // 用户关闭弹窗，留在当前企业
        });
        return;
      }

      try {
        // 先让服务端记住 current，再刷新；实际作用域由 x-enterprise-id 决定
        await switchEnterprise({ enterpriseId });
      } catch (error) {
        console.error('切换企业失败:', error);
        return;
      }
      window.location.href = buildEnterpriseSwitchUrl(enterpriseId);
    },

    /**
     * 初始化当前产品：用 switch-product 校验账号在当前企业下对「当前产品」的访问权限，
     * 顺带让服务端记录最近使用产品，并拿回当前企业下可访问的产品列表——产品切换器据此决定
     * 展示哪些入口、要不要展示。所以传的是当前产品而不是目标产品：拿目标产品去试探，
     * 会把「最近使用产品」错记成一个用户其实没进去的产品。
     * 当前产品由请求头 x-product-id 决定，token 不用重签，切换时直接跳目标产品即可。
     * 失败（无权限/网络异常）时列表留空，切换入口不展示，不打断主流程。
     * 猎头端不涉及多产品，直接跳过。
     */
    async fetchAccessibleProducts() {
      // 猎头端不涉及多产品，不需要这次校验，也不用记录最近使用产品
      // if (this.userSystem === System.agent) {
      //   this.enterpriseProducts = [];
      //   return;
      // }
      try {
        this.enterpriseProducts = (await switchProduct({ productId: this.productId })) || [];
      } catch (error) {
        console.error('获取可切换产品列表失败:', error);
        this.enterpriseProducts = [];
      }
    },

    checkRolesValidate(roles: any[]) {
      if (!roles || !roles.length) {
        // 没有角色，展示message弹窗
        ElMessage.error(i18n.global.t('common.noSystemAccess'))
        return false;
      } else {
        // 同一 productId 下会有多个企业的角色，先按当前企业过滤再归并；
        // 否则 reduce 只会留下数组里最后一条，头部就会展示成别的企业的角色
        const currentEnterpriseId = this.enterpriseId == null ? null : Number(this.enterpriseId);
        const scopedRoles = currentEnterpriseId == null
          ? roles
          : roles.filter((item: Account.RoleInfo) => Number(item.enterpriseId) === currentEnterpriseId);
        // 接口没返回 enterpriseId 时退回全量，保持旧行为
        const effectiveRoles = scopedRoles.length ? scopedRoles : roles;
        let rolesMap: {[key: string | number]: Account.RoleInfo} = effectiveRoles.reduce((pre, cur: Account.RoleInfo) => {
          pre[cur.productId] = cur;
          return pre;
        }, {})
        let curProductId = this.productId; 
        let fullPath = window.location.href;
        if (pathIsInRouterWhiteList(fullPath)) {
          // 如果是白名单的url，直接返回存在的 agent 或者 biz 的角色
          if (rolesMap[SystemProductId.agent]) {
            return rolesMap[SystemProductId.agent]
          } else if (rolesMap[SystemProductId.business]) {
            return rolesMap[SystemProductId.business]
          }
        } else if (curProductId == SystemProductId.agent && !rolesMap[curProductId]) {
          // 当前系统是agent系统，但是当前登录信息中没有agent的角色，认为需要重新登录
          console.log('当前系统是agent系统，但是当前登录信息中没有agent的角色，认为需要重新登录',)
          openNoAccessPermissionModal({
            message: i18n.global.t('common.noAccessPermission'),
            confirmButtonText: i18n.global.t('common.ok'),
            confirmButtonProps: {
              type: 'default'
            }
          }).then(res => {
            // window.location.href = import.meta.env.VITE_TALENT_URL;
            redirectToLogin({ redirectPath: '', delay: 0 });
          });
        } else if (rolesMap[curProductId]) {
          // 有当前系统的权限
          let role = rolesMap[curProductId];
          if (!validRoleIds.includes(role.id)) {
            // 有当前系统的权限但是是非法的角色
            console.log('有当前系统的权限但是是非法的角色')
            return false;
          }
          return rolesMap[curProductId];
        } else if (rolesMap[ProductId.TALENT]) {
          // 只有 TALENT 权限提示
          openNoAccessPermissionModal({
            message: i18n.global.t('common.noCareerAccess'),
            tips: i18n.global.t('common.contactAdminToEnable'),
            confirmButtonText: i18n.global.t('common.enterTalentSystem'),
          }).then(res => {
            // 带上当前企业：用户在企业 X 下没有 Career 权限但有 Talent 权限，
            // 应该进入「企业 X 的 Talent」，不带的话 Talent 会回落到它自己的默认企业
            window.location.href = withEnterpriseId(
              import.meta.env.VITE_TALENT_URL,
              this.enterpriseId
            );
          });
          // 无当前系统的权限，但是有talent权限
          return false;
        } else {
          redirectToLogin({ redirectPath: '', delay: 0 });
        }
        return false;
      }
    },
    checkHasOtherSystemAuth(roles: any) {
      if (!roles) return null;
      if (roles[ProductId.ATS]) return null;
      if (roles[ProductId.TALENT]) {
        console.log('profile',)
        
        return ProductId.TALENT;
      }
      return null;
    },
    getUserSystem() {
      // 第一优先级：检查环境变量配置的系统类型

      // 第二优先级：使用域名判断（非localhost环境）
      let host = location.host;
      // this.productId = SystemProductId.business;
      // return;
      if (!host.includes('localhost')) {
        // 非开发环境使用域名判断
        if (host.includes(import.meta.env.VITE_CRAEER_BIZ_HOSTNAME)) {
          this.userSystem = System.business;
          this.productId = SystemProductId.business;
          console.log('🌐 通过域名识别: Business');
        } else if (host.includes(import.meta.env.VITE_CRAEER_AGENT_HOSTNAME)) {
          this.userSystem = System.agent;
          this.productId = SystemProductId.agent;
          console.log('🌐 通过域名识别: Agent');
        }
      } else {
        // 第三优先级：开发环境使用角色id判断
        const configSystemType = getSystemType();
        this.userSystem = System.business;
        if (configSystemType) {
          this.userSystem = configSystemType;
          this.productId = SystemProductId[configSystemType];
          console.log(`🔧 使用配置的系统类型: ${configSystemType}`);
          return;
        } else {
          this.userSystem = System.business;
          this.productId = SystemProductId.business;
          console.log(`🔧 使用默认配置的系统类型: ${System.business}`);
        }
        // if (this.roleId) {
        //   if (SystemRole.business.includes(this.roleId)) {
        //     this.userSystem = System.business;
        //     console.log('🏢 通过角色识别: Business');
        //   } else if (SystemRole.agent.includes(this.roleId)) {
        //     this.userSystem = System.agent;
        //     console.log('🤝 通过角色识别: Agent');
        //   } else {
        //     this.userSystem = null;
        //     console.warn('⚠️ 无效的用户系统');
        //   }
        // }
      }
    },
    // 获取用户信息
    async getUserInfo() {
      try {
        if (!this.token) {
          throw new Error('未登录');
        } 
        // 判断所属系统
        this.getUserSystem()
        // 地址栏没带 company_id 时（首次进入/外部跳转），先取企业列表、用 current 定位当前企业，
        // 保证后续所有请求（含下面的 getProfileDetail）都带上正确的 x-enterprise-id。
        // my-enterprises 是账号级接口，不依赖 x-enterprise-id，可以先调。
        // 地址栏带了就以它为准，不能被服务端返回覆盖，否则切 tab 触发的 getUserInfo 会把企业改回去
        if (this.enterpriseId == null) {
          await this.fetchMyEnterprises();
          const currentEnterprise =
            this.enterprises.find((item) => item.current) || this.enterprises[0];
          if (currentEnterprise) {
            this.enterpriseId = currentEnterprise.enterpriseId;
          }
        }

        const profileDetail = await getProfileDetail();

        // 兜底：企业列表拿不到时，退回服务端 profile 里的企业
        if (this.enterpriseId == null && profileDetail.enterpriseId) {
          this.enterpriseId = Number(profileDetail.enterpriseId);
        }

        let resRoles = profileDetail.roles || [];
        let userRole: Account.RoleInfo | false = this.checkRolesValidate(resRoles);
        // console.log('userRole', userRole)

        if (!userRole) {
          return;
        }
        const enterpriseInfo = await getEnterpriseInfo();

        // console.log('userRole', userRole)
        // let userRole: Account.RoleInfo | false = {
        //   id: 7,
        //   name: 'ROLE_AGENT_ADMIN'
        // };
        
        // 将新API返回的数据映射到UserInfoResponse格式
        const userInfo: UserInfoResponse = {
          id: String(profileDetail.id),
          username: profileDetail.name,
          email: profileDetail.email,
          name: profileDetail.name,
          // 以权威的当前企业为准，避免与地址栏的 company_id 不一致
          enterpriseId: String(this.enterpriseId ?? profileDetail.enterpriseId),
          enterpriseName: enterpriseInfo?.name,
          roleInfo: {
            id: userRole?.id, // 根据实际情况映射角色
            name: userRole?.name
          },
          chatToken: profileDetail.chatToken,
          // Stream 的用户 id 由服务端生成（不透明串），chatToken 就是按它签的，不能前端拼
          streamChatId: profileDetail.streamChatId,
        };
        // console.log('userInfo', userInfo)
        // 保存旧角色以检测变化
        const oldRoles = userInfo.roleInfo?.id;
        this.roleIsValidate = !!userRole;
        this.profile = userInfo;
        this.roleId = userInfo.roleInfo?.id;
        this.isLoggedIn = true;

        // 地址栏带了 company_id 时上面没走过取列表的分支，这里补拉一次供切换器使用
        if (!this.enterprises.length) {
          await this.fetchMyEnterprises();
        }

        // 产品切换器的数据源：不 await，拿到结果后侧边栏自己响应式更新，不拖慢登录
        this.fetchAccessibleProducts();


        // 如果角色发生变化，重新生成权限路由以触发菜单更新
        // const { usePermissionStore } = await import('./permission');
        // const { asyncRoutes } = await import('@/router/routes/async');
        // const permissionStore = usePermissionStore();
        // permissionStore.generateRoutes(this.roleId, asyncRoutes);

        return userInfo;
      } catch (error: any) {
        console.log('error', error.message?.includes('401') || error.message?.includes('unauthorized'))
        // 如果获取用户信息失败，可能是 token 过期
        if (error.message?.includes('401') || error.message?.includes('unauthorized')) {
          this.logout();
        }
        throw error;
      }
    },
    async clearUserInfo() {

      try {
        // 关闭 StreamChat 长连接，防止旧用户 token 的 WebSocket 继续监听
        try {
          await useChatStore().disconnect()
        } catch {
          // 连接本就不存在时忽略
        }
        // 调用登出接口
        if (this.token) {
          await logoutApi();
        }
      } catch (error) {
        console.error('登出接口调用失败:', error);
      } finally {
        // 清空用户状态
        this.token = '';
        this.roleId = null;
        this.roles = [];
        this.permissions = [];
        this.profile = null;
        this.userSystem = null;
        this.enterpriseId = null;
        this.enterprises = [];
        this.enterpriseProducts = [];
        // this.productId = 2;
        // 清空本地存储
        localStorage.removeItem('lincId');
        deleteCookie();
      }
    },
    // 登出
    async logout(errorCode?: string) {
      let fullPath = window.location.href;
      if (!pathIsInRouterWhiteList(fullPath)) {
        if (errorCode === 'C0012') {
          ElMessage.info(i18n.global.t('common.authChange'));
        } else if (errorCode === 'C0007') {
          ElMessage.info(i18n.global.t('common.tokenExpired'));
        } else if (errorCode === 'logout') {
          fullPath = '';
          ElMessage.info(i18n.global.t('common.logouting'));
        } else {
          ElMessage.info(i18n.global.t('common.pleaseLogin'));
        }
        try {
          // 调用登出接口
          await this.clearUserInfo();
        } catch (error) {
          console.error('登出接口调用失败:', error);
        } finally {
          // 重置权限路由状态，确保重新登录时会重新生成路由
          // const { usePermissionStore } = await import('./permission');
          // const permissionStore = usePermissionStore();
          // permissionStore.resetRoutes();

          // 跳转到登录页（使用统一的登录跳转逻辑，支持外部登录URL）
          
        }
        // 白名单页面不做登录跳转
        // 带上当前企业，登录后能直接回到同一个企业的来源页
        redirectToLogin({ redirectPath: fullPath, delay: 0, enterpriseId: this.enterpriseId });

      
      } else {
        console.log('白名单页面不跳转登录')
      }
    },

    // 初始化用户状态（应用启动时调用）
    async initUserState() {
      if (this.token && !this.profile) {
        // TODO: 获取用户信息接口完成后，取消注释以下代码
        // try {
        //   await this.getUserInfo();
        // } catch (error) {
        //   console.error('初始化用户状态失败:', error);
        //   this.logout();
        // }
      }
    }
  }
});
